import type { PlannerSourceSearch } from './source-search-params';
import { useEffect, useRef } from 'react';

// Sticky planner product filters. The picker keeps its filters in the URL
// (`source*` params), so leaving the planner for another page drops them — the
// consultant came back to a reset list. Mirrors the stickiness legacy got from
// its localStorage-persisted filter store, and the shape of
// `@products/features/products-filters`'s `useDefaultCatalog` (the catalog half
// of the same behaviour, already sticky per-org).
//
// Scoped per itinerary, not per org: country/city/category describe the trip
// being planned, so carrying a Lisbon filter into a Spain itinerary would be
// wrong. `sourceCatalogId` is deliberately absent — `useDefaultCatalog` already
// owns it per-org, and duplicating it here would fight that hook.
//
// sessionStorage, not localStorage: this survives page navigation within the
// working session (the reported case) without growing an unbounded per-itinerary
// key set, or resurrecting a filter set from weeks ago.

const STORAGE_KEY_PREFIX = 'planner:source-filters';

const storageKey = (itineraryId: string) =>
  `${STORAGE_KEY_PREFIX}:${itineraryId}`;

// The picker's visible filter controls. Deliberately excluded: `sourcePage`
// (position, not a filter), `similarTo`/`sourceView` (modes — restoring one
// would strand the consultant in a view they never chose), and the map
// bbox/region (they filter the list invisibly when the map is closed).
const STICKY_KEYS = [
  'sourceSearch',
  'sourceCategory',
  'sourceSubcategory',
  'sourceCountry',
  'sourceCity',
  'sourceSupplierName',
  'sourceValueTier',
] as const;

type StickyKey = (typeof STICKY_KEYS)[number];
export type StickySourceFilters = Pick<PlannerSourceSearch, StickyKey>;

const isEmpty = (v: unknown): boolean =>
  v === undefined || v === '' || (Array.isArray(v) && v.length === 0);

export const pickStickyFilters = (
  search: PlannerSourceSearch
): StickySourceFilters => {
  const out: Record<string, unknown> = {};
  for (const k of STICKY_KEYS) {
    if (!isEmpty(search[k])) out[k] = search[k];
  }
  return out as StickySourceFilters;
};

export const hasAnyStickyFilter = (f: StickySourceFilters): boolean =>
  Object.keys(f).length > 0;

// Re-validate on read: sessionStorage is user-writable and may hold a shape
// from an older release, so parse it through the same gate as the URL.
export const parseStickyFilters = (raw: string): StickySourceFilters | null => {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return pickStickyFilters(parsed as PlannerSourceSearch);
  } catch {
    return null;
  }
};

const readStored = (itineraryId: string): StickySourceFilters | null => {
  try {
    const raw = window.sessionStorage.getItem(storageKey(itineraryId));
    return raw === null ? null : parseStickyFilters(raw);
  } catch {
    return null;
  }
};

const writeStored = (itineraryId: string, f: StickySourceFilters): void => {
  try {
    window.sessionStorage.setItem(storageKey(itineraryId), JSON.stringify(f));
  } catch {
    // Non-fatal: storage blocked (private mode). Filters just stay per-visit.
  }
};

// `search` is the live URL search; `onRestore` writes a remembered set back to
// it. Restore happens once per mount, and only when the URL carries no filters
// of its own — a deep link (or the agent panel's `apply_filters`) always wins.
export const useStickySourceFilters = ({
  itineraryId,
  search,
  onRestore,
}: {
  itineraryId: string;
  search: PlannerSourceSearch;
  onRestore: (filters: StickySourceFilters) => void;
}): void => {
  const restoredRef = useRef(false);
  const lastWrittenRef = useRef<string | null>(null);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;

  useEffect(() => {
    if (!itineraryId) return;

    const current = pickStickyFilters(search);

    // Skip redundant writes — the URL re-parses each render, so `search`
    // changes identity without changing value.
    const persist = (f: StickySourceFilters) => {
      const key = JSON.stringify(f);
      if (key === lastWrittenRef.current) return;
      lastWrittenRef.current = key;
      writeStored(itineraryId, f);
    };

    if (restoredRef.current) {
      persist(current);
      return;
    }
    restoredRef.current = true;

    if (hasAnyStickyFilter(current)) {
      persist(current);
      return;
    }

    const stored = readStored(itineraryId);
    if (stored && hasAnyStickyFilter(stored)) {
      lastWrittenRef.current = JSON.stringify(stored);
      onRestoreRef.current(stored);
    }
  }, [itineraryId, search]);
};
